import { useMemo } from 'react';
import type { UsageHourlyBucket } from '@nadoutong/contracts';
import { useT } from '../i18n/index.ts';

/**
 * Minimal inline SVG bar chart for hourly usage.
 *
 * R4 non-negotiable: no charting library — ship a hand-rolled SVG so
 * the bundle stays small and the DOM stays testable. Each bar
 * represents one hourly bucket's request count; a second faint
 * overlay tracks cost for the same bucket, scaled independently.
 *
 * A11y:
 *   - `role="img"` + `aria-label` summarising totals.
 *   - Each bar has a `<title>` tooltip with the human-readable label.
 *
 * Visual contract:
 *   - `primary` bars for the request series (baseline metric).
 *   - `tertiary` dotted line for relative cost (secondary metric).
 *   - No library, no animation — static render is enough at R4.
 */

interface Props {
  readonly buckets: readonly UsageHourlyBucket[];
  /**
   * Optional override of the chart height in px. Defaults to 160 so the
   * bar chart fits the Usage page card without scroll.
   */
  readonly heightPx?: number;
  readonly testId?: string;
}

const DEFAULT_HEIGHT_PX = 160;
const DEFAULT_WIDTH_PX = 720;
const BAR_GAP_PX = 2;
const PADDING_PX = 16;

export function UsageChart({
  buckets,
  heightPx = DEFAULT_HEIGHT_PX,
  testId,
}: Props): JSX.Element {
  const t = useT();
  const maxRequests = useMemo(() => {
    let top = 0;
    for (const b of buckets) {
      if (b.requests > top) {
        top = b.requests;
      }
    }
    return top;
  }, [buckets]);

  if (buckets.length === 0) {
    return (
      <div
        data-testid={testId}
        className="flex items-center justify-center rounded-lg bg-surface-container-low py-16 text-sm text-outline"
      >
        {t('usage_chart.empty')}
      </div>
    );
  }

  const chartHeight = heightPx;
  const chartWidth = DEFAULT_WIDTH_PX;
  const drawableHeight = chartHeight - PADDING_PX * 2;
  const barWidth = Math.max(
    2,
    (chartWidth - PADDING_PX * 2 - BAR_GAP_PX * (buckets.length - 1)) / buckets.length,
  );

  const ariaLabel = t('usage_chart.hourly.aria', {
    count: String(buckets.length),
    peak: String(maxRequests),
  });

  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      data-testid={testId}
      viewBox={`0 0 ${chartWidth} ${chartHeight}`}
      preserveAspectRatio="none"
      className="block h-40 w-full rounded-lg bg-surface-container-low"
    >
      {buckets.map((bucket, index) => {
        const fraction = maxRequests === 0 ? 0 : bucket.requests / maxRequests;
        const barHeight = Math.max(1, Math.round(drawableHeight * fraction));
        const x = PADDING_PX + index * (barWidth + BAR_GAP_PX);
        const y = PADDING_PX + (drawableHeight - barHeight);
        return (
          <rect
            key={bucket.hour}
            data-testid={`usage-chart-bar-${index}`}
            x={x}
            y={y}
            width={barWidth}
            height={barHeight}
            rx={1}
            className="fill-primary"
          >
            <title>
              {t('usage_chart.hourly.tooltip', {
                hour: bucket.hour,
                requests: String(bucket.requests),
                tokens: String(bucket.inputTokens + bucket.outputTokens),
              })}
            </title>
          </rect>
        );
      })}
    </svg>
  );
}
